import { replay, type Turn } from "./rules.js";

/**
 * Local state: groups, their rosters, the game in progress and finished games.
 *
 * Local-first, like everything else here (PRD 8.2). A group is created and
 * usable with no network at all; the server hears about it on the next sync.
 * This is the single source of truth the screens read from, and it is written
 * to localStorage on every change so closing the app loses nothing.
 */

export interface Player {
  id: string;
  name: string;
}

export interface Group {
  id: string;
  name: string;
  players: Player[];
  /**
   * Section 7: celebrations can get noisy, so a group can cut them back to the
   * rare ones. Absent means "all", so groups saved before this existed keep
   * the documented default.
   */
  celebrations?: "all" | "rare";
}

export interface Game {
  id: string;
  groupId: string;
  /** Player ids in playing order. */
  order: string[];
  turns: Turn[];
  startedAt: number;
  finishedAt?: number;
  /**
   * Soft delete (8.3). The server keeps a deleted game for 30 days; the local
   * copy has to as well, or the only copy the client can actually read is gone
   * the moment somebody taps the wrong row -- sync is one-way today, so there
   * is nothing to fetch it back from.
   */
  deletedAt?: number;
}

export interface State {
  groups: Group[];
  /** At most one game in progress; a second would need a second scoring screen. */
  game: Game | null;
  history: Game[];
}

export const emptyState = (): State => ({ groups: [], game: null, history: [] });

export function makeId(prefix: string, seed: number): string {
  return `${prefix}-${seed.toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
}

/** FR-1.1 */
export function addGroup(state: State, name: string, now: number): State {
  const trimmed = name.trim();
  if (!trimmed) return state;
  return {
    ...state,
    groups: [...state.groups, { id: makeId("grp", now), name: trimmed, players: [] }],
  };
}

/** FR-1.5: removing a group takes its games with it. */
export function removeGroup(state: State, groupId: string): State {
  return {
    ...state,
    groups: state.groups.filter((g) => g.id !== groupId),
    history: state.history.filter((g) => g.groupId !== groupId),
    game: state.game?.groupId === groupId ? null : state.game,
  };
}

export function renameGroup(state: State, groupId: string, name: string): State {
  const trimmed = name.trim();
  if (!trimmed) return state;
  return {
    ...state,
    groups: state.groups.map((g) => (g.id === groupId ? { ...g, name: trimmed } : g)),
  };
}

/** Fold case and accents so "Jörg" and "jorg" are the same person. */
function normalise(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/**
 * FR-1.3, and the identity half of FR-7.2.
 *
 * A player already known under this name -- in any group -- keeps their id, so
 * the same Anna is one person in the all-time table rather than one row per
 * group. FR-7.2 asks for explicit identity rather than the prototype's
 * match-by-name at read time: the name is resolved to an id once, here, and
 * everything downstream joins on the id. That mirrors what the server does with
 * persons, and it means renaming one Anna renames her everywhere, which is the
 * point of FR-1.4.
 */
export function addPlayer(state: State, groupId: string, name: string, now: number): State {
  const trimmed = name.trim();
  if (!trimmed) return state;

  const group = state.groups.find((g) => g.id === groupId);
  if (!group) return state;
  // Already on this roster: nothing to do.
  if (group.players.some((p) => normalise(p.name) === normalise(trimmed))) return state;

  const known = state.groups
    .flatMap((g) => g.players)
    .find((p) => normalise(p.name) === normalise(trimmed));
  const player = known ?? { id: makeId("ply", now), name: trimmed };

  return {
    ...state,
    groups: state.groups.map((g) =>
      g.id === groupId ? { ...g, players: [...g.players, player] } : g,
    ),
  };
}

/**
 * FR-1.4: renaming is retroactive. Games reference the player id, never the
 * name, so history and statistics pick the new name up on their own.
 */
export function renamePlayer(state: State, playerId: string, name: string): State {
  const trimmed = name.trim();
  if (!trimmed) return state;
  return {
    ...state,
    groups: state.groups.map((g) => ({
      ...g,
      players: g.players.map((p) => (p.id === playerId ? { ...p, name: trimmed } : p)),
    })),
  };
}

/**
 * Remove a player from a group's roster. Past games keep the reference, so
 * their history survives -- a player who has left still won what they won.
 */
export function removePlayer(state: State, groupId: string, playerId: string): State {
  return {
    ...state,
    groups: state.groups.map((g) =>
      g.id === groupId ? { ...g, players: g.players.filter((p) => p.id !== playerId) } : g,
    ),
  };
}

export function startGame(state: State, groupId: string, order: string[], now: number): State {
  return {
    ...state,
    game: { id: makeId("gam", now), groupId, order, turns: [], startedAt: now },
  };
}

/** Move a finished game into history. FR-5.4: an unfinished one is just dropped. */
export function archiveGame(state: State, now: number): State {
  const game = state.game;
  if (!game) return state;
  const finished = replay(game.order, game.turns).finished;
  return {
    ...state,
    game: null,
    history: finished ? [{ ...game, finishedAt: now }, ...state.history] : state.history,
  };
}

/** Section 7: quieten a group's celebrations, or turn them back up. */
export function setCelebrations(state: State, groupId: string, mode: "all" | "rare"): State {
  return {
    ...state,
    groups: state.groups.map((g) => (g.id === groupId ? { ...g, celebrations: mode } : g)),
  };
}

/** FR-5.4: drop the game in progress without recording it anywhere. */
export function discardGame(state: State): State {
  return { ...state, game: null };
}

/** Days a deleted game stays recoverable, per 8.3. */
export const RETENTION_DAYS = 30;

export function deleteHistoricGame(state: State, gameId: string, now: number): State {
  return {
    ...state,
    history: state.history.map((g) => (g.id === gameId ? { ...g, deletedAt: now } : g)),
  };
}

export function restoreGame(state: State, gameId: string): State {
  return {
    ...state,
    history: state.history.map((g) => {
      if (g.id !== gameId) return g;
      const { deletedAt: _removed, ...rest } = g;
      return rest;
    }),
  };
}

/** Games still in play for statistics and history. */
export function liveGames(state: State): Game[] {
  return state.history.filter((g) => g.deletedAt === undefined);
}

/** Games in the bin, most recently deleted first. */
export function deletedGames(state: State): Game[] {
  return state.history
    .filter((g) => g.deletedAt !== undefined)
    .sort((a, b) => (b.deletedAt ?? 0) - (a.deletedAt ?? 0));
}

/** Drop anything past its 30 days. Called on load, so it happens exactly once. */
export function purgeExpired(state: State, now: number): State {
  const cutoff = now - RETENTION_DAYS * 24 * 60 * 60 * 1000;
  const kept = state.history.filter((g) => g.deletedAt === undefined || g.deletedAt > cutoff);
  return kept.length === state.history.length ? state : { ...state, history: kept };
}

/** Every name the app knows, so a screen can resolve an id from any group. */
export function nameIndex(state: State): Record<string, string> {
  const out: Record<string, string> = {};
  for (const group of state.groups) {
    for (const player of group.players) out[player.id] = player.name;
  }
  return out;
}

/** FR-1.2: what the group list shows about each group. */
export interface GroupSummary {
  group: Group;
  players: number;
  games: number;
  live: boolean;
}

export function summarise(state: State): GroupSummary[] {
  return state.groups.map((group) => ({
    group,
    players: group.players.length,
    games: state.history.filter((g) => g.groupId === group.id && g.deletedAt === undefined)
      .length,
    live: state.game?.groupId === group.id,
  }));
}
